import { capitalizeFirstLetter } from "../string_convert_helper";
import { getFindingLabel, getFindingTitle } from "./findingFilters";
import { ReportItemList } from "./reportEditAccess";

// How a report's page shows one kind of item: an audit's findings, or the
// follow-up actions of an incident, MoC, non-conformance or OFI report. The
// items panel (components/Reporting/items) takes everything that differs
// between the two from here.
export type ReportItemKind = {
	// The report field that holds the items.
	listKey: ReportItemList["name"];
	// "finding" and "findings": how the panel's own words name the items.
	noun: string;
	plural: string;
	// Names the panel for screen readers.
	label: string;
	// How the report's page names the item at this position, such as
	// "Finding 3".
	getTitle: (index: number) => string;
	// What the drawer calls it, such as "#Findings 3". The search finds an
	// item by either name.
	getDrawerLabel: (index: number) => string;
	// The item's main text.
	descriptionField: "findings_description" | "description";
	// What is recorded once the item is completed, and its label.
	outcomeField: "what_was_done" | "closing_comment";
	outcomeLabel: string;
	// Audits only: the recommended action, a section of its own on each card.
	recommendedActionField?: "recommended_actions";
	typeField: "finding_type" | "action_type";
	// How a stored type reads, where it isn't stored as it reads.
	formatType?: (type: string) => string;
	// Audits only: a Non-Conformity finding shows its criticality.
	hasCriticality: boolean;
	// An item already completed when the report was written: its tag, and the
	// note on its Completed stage.
	alreadyCompletedTag: string;
	alreadyCompletedNote: string;
	// Where each browser remembers the Cards / List choice.
	viewStorageKey: string;
	// Shown when the report has no items.
	emptyText: string;
	// Audits overwrite approved_on with the final approval, so once an audit
	// is closed it no longer dates the initial review.
	finalApprovalOverwritesApprovedOn: boolean;
};

const FINDINGS: ReportItemKind = {
	listKey: "findings",
	noun: "finding",
	plural: "findings",
	label: "Findings",
	getTitle: getFindingTitle,
	getDrawerLabel: getFindingLabel,
	descriptionField: "findings_description",
	outcomeField: "what_was_done",
	outcomeLabel: "What was done",
	recommendedActionField: "recommended_actions",
	typeField: "finding_type",
	hasCriticality: true,
	alreadyCompletedTag: "Closed during audit",
	alreadyCompletedNote: "During the audit",
	// The key the audit page has always used, so nobody's choice resets.
	viewStorageKey: "audit-findings-view",
	emptyText: "No findings recorded",
	finalApprovalOverwritesApprovedOn: true,
};

const FOLLOW_UP_ACTIONS: ReportItemKind = {
	listKey: "follow_up_actions",
	noun: "action",
	plural: "actions",
	label: "Follow-up actions",
	getTitle: (index) => `Action ${index + 1}`,
	// As the report forms label their actions.
	getDrawerLabel: (index) => `#Action ${index + 1}`,
	descriptionField: "description",
	outcomeField: "closing_comment",
	outcomeLabel: "Closing comment",
	typeField: "action_type",
	// Stored as "corrective", "preventive" or "compensating".
	formatType: capitalizeFirstLetter,
	hasCriticality: false,
	alreadyCompletedTag: "Already completed",
	alreadyCompletedNote: "Already completed",
	viewStorageKey: "report-actions-view",
	emptyText: "No follow-up actions recorded",
	finalApprovalOverwritesApprovedOn: false,
};

export const REPORT_ITEM_KINDS: Record<ReportItemList["name"], ReportItemKind> =
	{
		findings: FINDINGS,
		follow_up_actions: FOLLOW_UP_ACTIONS,
	};
